<template>
    <div class="org-flags-for-org">
        <p
            v-if="isLoading"
            class="section-help"
            data-test-id="org-flags-for-org-loading"
        >
            Loading organization feature flags...
        </p>

        <p
            v-else-if="loadError"
            class="section-error"
            data-test-id="org-flags-for-org-error"
        >
            {{ loadError }}
        </p>

        <p
            v-else-if="!organization"
            class="section-help"
            data-test-id="org-flags-for-org-hint"
        >
            Select an organization to see its feature flags.
        </p>

        <div
            v-else
            class="org-flags-for-org__list"
            data-test-id="org-flags-for-org-list"
        >
            <div
                v-for="column in columns"
                :key="column.baseName"
                class="org-flags-for-org__row"
                :class="{ 'org-flags-for-org__row--override': isOverride(column) }"
                :data-test-id="`org-flag-row-${column.baseName}`"
            >
                <!--
                    The slot is always rendered, holding the trash can's width
                    even when this row has no override to delete, so every
                    toggle on the list starts at the same x position.
                -->
                <div
                    class="org-flags-for-org__delete-slot"
                    :data-test-id="`org-flag-delete-slot-${column.baseName}`"
                >
                    <button
                        v-if="isOverride(column)"
                        type="button"
                        class="org-flags-for-org__delete icon-delete"
                        title="Delete Override"
                        :aria-label="`Delete Override for ${column.label}`"
                        :disabled="!canUpdate || isSaving(column)"
                        :data-test-id="`org-flag-delete-${column.baseName}`"
                        :data-focus-key="`org-flag-delete-${column.baseName}`"
                        @click="$emit('delete', { baseName: column.baseName })"
                    />
                </div>

                <slider-toggle-field
                    :data-test-id="`org-flag-toggle-${column.baseName}`"
                    :focus-key="`org-flag-toggle-${column.baseName}`"
                    :name="column.baseName"
                    :label="column.label"
                    :value="valueFor(column)"
                    :disabled="!canUpdate || isSaving(column)"
                    @update="emitToggle(column, $event)"
                />
            </div>
        </div>
    </div>
</template>

<script>
import SliderToggleField from '@/components/SliderToggleField.vue'

export default {
    name: 'OrganizationFlagsForOrg',

    components: { SliderToggleField },

    props: {
        columns: {
            type: Array,
            default: () => [],
        },
        organization: {
            type: Object,
            default: null,
        },
        savingFlagNames: {
            type: Array,
            default: () => [],
        },
        canUpdate: {
            type: Boolean,
            default: false,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        loadError: {
            type: String,
            default: null,
        },
    },

    emits: ['toggle', 'delete'],

    methods: {
        valueFor(column) {
            return Boolean(this.organization.flags[column.baseName])
        },

        // Highlight follows the endpoint's overrides field, the same field the
        // override lists above read, so the two agree by construction.
        isOverride(column) {
            return this.organization.overrides.includes(column.baseName)
        },

        isSaving(column) {
            return this.savingFlagNames.includes(column.baseName)
        },

        emitToggle(column, enabled) {
            this.$emit('toggle', {
                baseName: column.baseName,
                enabled,
            })
        },
    },
}
</script>

<style scoped lang="scss">
.org-flags-for-org__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 640px;
}

.org-flags-for-org__row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border-left: 3px solid transparent;
    border-radius: 4px;

    // The tint alone composites to 1.26:1 against the row background, which
    // is far too weak to carry the meaning on its own. The trash can is the
    // real indicator; the left rule adds a 4.89:1 edge to find it by.
    &--override {
        background: rgba(249, 106, 98, 0.22);
        border-left-color: #ffa8a2;
    }
}

.org-flags-for-org__delete-slot {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 22px;
    width: 22px;
}

.org-flags-for-org__delete {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: #ffa8a2;
    font-size: 22px;
    line-height: 1;

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
}

.section-help {
    color: #c9c9c9;
    font-family: 'Avenir Book', sans-serif;
}

.section-error {
    color: #ffa8a2;
    font-family: 'Avenir Heavy', sans-serif;
}
</style>
